<template>
  <div class="workspace-detail-container" v-loading="pageLoading">
    <!-- Top Workspace Summary Card -->
    <div v-if="workspace" class="workspace-summary-card">
      <div class="summary-left">
        <el-button link @click="goBack" class="back-link">
          <el-icon><ArrowLeft /></el-icon> 返回列表
        </el-button>
        <div class="ws-info">
          <h2>{{ workspace.name }}</h2>
          <div class="ws-meta">
            <el-tag :type="getStatusType(workspace.status)" size="small">
              {{ getStatusText(workspace.status) }}
            </el-tag>
            <span class="meta-item">班级数：{{ workspace.config?.class_count || 0 }}</span>
            <span class="meta-item">学生总数：{{ totalStudents }}</span>
            <span class="meta-item">规则总数：{{ rules.length }}</span>
          </div>
        </div>
      </div>
      <div class="summary-right">
        <!-- Main Quick Action Buttons -->
        <el-button-group>
          <el-button type="success" :disabled="students.length === 0" @click="activeTab = 'divide'">
            <el-icon><Cpu /></el-icon> 开始分班
          </el-button>
          <el-button type="primary" :disabled="workspace.status !== 'success'" @click="activeTab = 'result'">
            <el-icon><DataAnalysis /></el-icon> 查看结果
          </el-button>
          <el-button type="warning" :disabled="workspace.status !== 'success'" @click="toggleSunshineLive(true)">
            <el-icon><FullScreen /></el-icon> 投屏展示
          </el-button>
        </el-button-group>
      </div>
    </div>

    <div v-if="workspace" class="guide-panel">
      <div class="guide-title">
        <h3>{{ currentGuide.title }}</h3>
        <p>{{ currentGuide.desc }}</p>
      </div>
      <div class="guide-steps">
        <button
          v-for="step in guideSteps"
          :key="step.key"
          class="guide-step"
          :class="{ active: step.active, done: step.done }"
          type="button"
          @click="activeTab = step.tab"
        >
          <span class="step-index">{{ step.index }}</span>
          <span class="step-copy">
            <strong>{{ step.title }}</strong>
            <small>{{ step.hint }}</small>
          </span>
        </button>
      </div>
    </div>

    <!-- Active Tab Menu -->
    <el-tabs v-model="activeTab" class="custom-tabs" type="card" @tab-change="handleTabChange">
      <!-- Tab 1: Students List -->
      <el-tab-pane name="students" label="1 学生名单" lazy>
        <div class="tab-action-bar">
          <div class="bar-left">
            <el-input
              v-model="searchQuery"
              placeholder="输入姓名搜索..."
              clearable
              :prefix-icon="Search"
              style="width: 240px"
              @input="filterStudents"
            />
            <el-select v-model="filterClass" placeholder="按班级筛选" clearable style="width: 140px" @change="filterStudents">
              <el-option label="全部学生" value="" />
              <el-option label="未分配班级" value="unassigned" />
              <el-option v-for="c in allClassNames" :key="c" :label="c" :value="c" />
            </el-select>
          </div>
          <div class="bar-right">
            <el-button type="warning" plain @click="downloadTemplate">
              <el-icon><Download /></el-icon> 下载导入模板
            </el-button>
            <el-button type="primary" @click="importDialogVisible = true">
              <el-icon><Upload /></el-icon> Excel批量导入
            </el-button>
            <el-button type="success" plain @click="openStudentDialog(null)">
              <el-icon><Plus /></el-icon> 手动添加学生
            </el-button>
            <el-button type="danger" plain @click="clearAllStudents">
              <el-icon><Delete /></el-icon> 清空名册
            </el-button>
          </div>
        </div>

        <el-table :data="paginatedStudents" border stripe style="width: 100%; margin-top: 15px;">
          <el-table-column prop="student_id" label="学号/考号" width="130" />
          <el-table-column prop="name" label="姓名" width="120" />
          <el-table-column prop="gender" label="性别" width="90">
            <template #default="{ row }">
              <el-tag :type="getGenderTagType(row.gender)" size="small">
                {{ row.gender }}
              </el-tag>
            </template>
          </el-table-column>
          <!-- 动态展示各科分数，类似 Excel -->
          <el-table-column
            v-for="subj in availableSubjects"
            :key="'score_' + subj"
            :label="subj"
            width="100"
            align="center"
          >
            <template #default="{ row }">
              <span>{{ row.scores?.[subj] !== undefined ? row.scores[subj] : '-' }}</span>
            </template>
          </el-table-column>

          <el-table-column label="特色标签">
            <template #default="{ row }">
              <div class="tags-preview">
                <el-tag v-for="tag in row.custom_tags" :key="tag" size="small" type="success" effect="plain" class="custom-tag">
                  {{ tag }}
                </el-tag>
                <span v-if="!row.custom_tags || row.custom_tags.length === 0" class="muted-text">-</span>
              </div>
            </template>
          </el-table-column>

          <!-- 动态展示自定义扩展字段，类似 Excel -->
          <el-table-column
            v-for="key in allExtraKeys"
            :key="'extra_' + key"
            :label="key"
            min-width="120"
            align="center"
          >
            <template #default="{ row }">
              <span>{{ row.extra_fields?.[key] !== undefined ? row.extra_fields[key] : '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="assigned_class" label="分配班级" width="120">
            <template #default="{ row }">
              <span :class="{ 'assigned-text': row.assigned_class, 'unassigned-text': !row.assigned_class }">
                {{ row.assigned_class || '未分配' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="分班锁定" width="110" align="center">
            <template #default="{ row }">
              <el-switch
                v-model="row.is_locked"
                :disabled="!row.assigned_class"
                @change="toggleStudentLock(row)"
              />
            </template>
          </el-table-column>
          <el-table-column label="操作" width="150" align="center">
            <template #default="{ row }">
              <el-button-group>
                <el-button size="small" :icon="Edit" @click="openStudentDialog(row)" />
                <el-button size="small" type="danger" :icon="Delete" @click="deleteStudent(row.id)" />
              </el-button-group>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-row">
          <el-pagination
            v-model:current-page="currentPage"
            v-model:page-size="pageSize"
            :page-sizes="pageSizesOption"
            layout="total, sizes, prev, pager, next, jumper"
            :total="totalStudents"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>

        <div class="step-navigation-buttons">
          <el-button disabled>上一步</el-button>
          <el-button type="primary" @click="activeTab = 'rules'">下一步：设置特殊要求</el-button>
        </div>
      </el-tab-pane>

      <!-- Tab 2: Rules Config -->
      <el-tab-pane name="rules" label="2 特殊要求" lazy>
        <div class="rules-container">
          <div class="rules-left">
            <div class="card-title-row">
            <h3>特殊分班要求</h3>
              <div class="rules-actions">
                <el-button type="success" size="small" plain @click="downloadBatchPreAssignTemplate">
                  <el-icon><Download /></el-icon> 下载批量模板
                </el-button>
                <el-button type="success" size="small" @click="batchPreAssignVisible = true">
                  <el-icon><Upload /></el-icon> 批量指定班级
                </el-button>
                <el-button type="info" size="small" plain @click="importRulesVisible = true">
                  <el-icon><Upload /></el-icon> 导入规则
                </el-button>
                <el-button type="info" size="small" plain @click="exportRules">
                  <el-icon><Download /></el-icon> 导出规则
                </el-button>
                <el-button type="primary" size="small" @click="openRuleDialog">
                  <el-icon><Plus /></el-icon> 添加要求
                </el-button>
              </div>
            </div>

            <!-- Rules Table -->
            <el-table :data="rules" border style="width: 100%; margin-top: 15px;">
              <el-table-column prop="rule_type" label="规则类型" width="140">
                <template #default="{ row }">
                  <el-tag :type="getRuleTagType(row.rule_type)">
                    {{ getRuleTypeText(row.rule_type) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="涉及学生">
                <template #default="{ row }">
                  <span>{{ row.student_names.join('、') }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="target_class" label="目标班级" width="120">
                <template #default="{ row }">
                  {{ row.target_class || '-' }}
                </template>
              </el-table-column>
              <el-table-column prop="description" label="备注说明" />
              <el-table-column label="操作" width="100" align="center">
                <template #default="{ row }">
                  <el-button size="small" type="danger" :icon="Delete" @click="deleteRule(row.id)" />
                </template>
              </el-table-column>
            </el-table>
          </div>

          <div class="rules-right">
            <h3>要求检查</h3>
            <div class="conflict-check-card" :class="{ 'no-conflict': !conflictError, 'has-conflict': conflictError }">
              <template v-if="conflictError">
                <el-icon class="status-icon"><Warning /></el-icon>
                <div class="status-text">
                  <h4>有要求互相冲突</h4>
                  <p>{{ conflictError }}</p>
                </div>
              </template>
              <template v-else>
                <el-icon class="status-icon"><CircleCheck /></el-icon>
                <div class="status-text">
                  <h4>当前可以继续分班</h4>
                  <p>如果有指定班级、同班、分开等要求，系统会在分班时一起考虑。</p>
                </div>
              </template>
            </div>
            <div class="rule-hint-box">
              <h4>常见要求说明：</h4>
              <ul>
                <li><strong>指定预分班</strong>：指定某些学生强行分入某班级。</li>
                <li><strong>不分入某班</strong>：指定某些学生绝对不进入某班级.</li>
                <li><strong>同班组(绑定)</strong>：多名学生强行绑在一起，分入同一个班级（如双胞胎、好友）。</li>
                <li><strong>互斥组(打散)</strong>：多名学生强行错开，必须分配到不同班级（如调皮捣蛋组）。</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="step-navigation-buttons">
          <el-button @click="activeTab = 'students'">上一步：学生名单</el-button>
          <el-button type="primary" @click="activeTab = 'divide'">下一步：开始分班</el-button>
        </div>
      </el-tab-pane>

      <!-- Tab 3: Algorithm Running -->
      <el-tab-pane name="divide" label="3 开始分班" lazy>
        <div class="algorithm-container" :class="{ 'single-column': !showProgressPanel }">
          <!-- Setup options -->
          <div class="algo-setup-card">
            <h3>分班前确认</h3>
            <el-form label-position="top" style="margin-top: 20px;">
              <el-form-item label="需要尽量均衡的成绩科目">
                <el-checkbox-group v-model="selectedSubjects" @change="saveWorkspaceConfig">
                  <el-checkbox v-for="subj in availableSubjects" :key="subj" :label="subj">{{ subj }}</el-checkbox>
                </el-checkbox-group>
                <div v-if="availableSubjects.length === 0" class="muted-text">还没有设置分班科目。可以回到分班列表的“科目设置”维护，也可以先只按人数、男女比例和要求分班。</div>
              </el-form-item>

              <!-- 均衡选项配置 -->
              <div v-if="workspaceConfig.weights" class="weight-settings-card" style="margin-top: 15px; margin-bottom: 20px; padding: 15px; background-color: #f8fafc; border-radius: 8px; border: 1px solid #e2e8f0;">
                <h4 style="margin-top: 0; margin-bottom: 12px; display: flex; align-items: center; color: #334155; font-size: 14px;">
                  <el-icon style="margin-right: 6px; color: #4f46e5;"><Operation /></el-icon>
                  分班均衡偏好设置
                </h4>
                <div style="display: flex; flex-wrap: wrap; gap: 20px; padding: 5px 10px;">
                  <el-checkbox v-model="balanceScoreEnabled">
                    <span style="font-size: 14px; font-weight: 550; color: #374151;">均衡学生成绩</span>
                    <span style="font-size: 12px; color: #6b7280; margin-left: 5px;">(让各班级平均分尽可能接近)</span>
                  </el-checkbox>
                  <el-checkbox v-model="balanceSurnameEnabled">
                    <span style="font-size: 14px; font-weight: 550; color: #374151;">均衡姓氏分布</span>
                    <span style="font-size: 12px; color: #6b7280; margin-left: 5px;">(避免同姓氏学生扎堆在某些班)</span>
                  </el-checkbox>
                  <el-checkbox v-model="balanceTopStudentsEnabled">
                    <span style="font-size: 14px; font-weight: 550; color: #374151;">尖子生分布保底</span>
                    <span style="font-size: 12px; color: #6b7280; margin-left: 5px;">(让各科目Top 10%尖子生均匀分布)</span>
                  </el-checkbox>
                  <el-checkbox v-model="balanceGenderEnabled">
                    <span style="font-size: 14px; font-weight: 550; color: #374151;">均衡男女比例</span>
                    <span style="font-size: 12px; color: #6b7280; margin-left: 5px;">(让各班男女比例尽可能接近)</span>
                  </el-checkbox>
                  <el-checkbox v-model="balanceTagsEnabled">
                    <span style="font-size: 14px; font-weight: 550; color: #374151;">均衡特色标签</span>
                    <span style="font-size: 12px; color: #6b7280; margin-left: 5px;">(让特长生、班干部等均匀分布)</span>
                  </el-checkbox>
                </div>
              </div>

              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="分班方式">
                    <el-select v-model="workspaceConfig.algorithm" @change="saveWorkspaceConfig" style="width: 100%">
                      <el-option label="推荐：更均衡，适合正式使用" value="anneal" />
                      <el-option label="快速：先生成初稿" value="snake" />
                    </el-select>
                  </el-form-item>
                </el-col>
                <el-col :span="12" v-if="workspaceConfig.algorithm === 'anneal'">
                  <el-form-item label="均衡细致程度">
                    <el-select v-model="workspaceConfig.iterations" @change="saveWorkspaceConfig" style="width: 100%">
                      <el-option label="快一些" :value="5000" />
                      <el-option label="推荐" :value="20000" />
                      <el-option label="更细致" :value="50000" />
                    </el-select>
                  </el-form-item>
                </el-col>
              </el-row>

              <!-- 分班模式选择 -->
              <el-form-item label="是否需要分层班" style="margin-top: 15px;">
                <el-radio-group v-model="workspaceConfig.divisionMode" @change="handleModeChange">
                  <el-radio-button label="parallel">不需要，全部平行分班</el-radio-button>
                  <el-radio-button label="tiered">需要，例如强基/实验/平行</el-radio-button>
                </el-radio-group>
              </el-form-item>

              <!-- 树状分层分班配置面板 -->
              <div v-if="workspaceConfig.divisionMode === 'tiered'" class="tiered-config-container" style="margin-bottom: 20px;">
                <div v-for="(tier, idx) in workspaceConfig.tiers" :key="idx" class="tier-card">
                  <div class="tier-card-header">
                    <span class="tier-title">
                      <el-icon style="margin-right: 5px; color: #409eff;"><Cpu /></el-icon>
                      第 {{ idx + 1 }} 层：{{ tier.name || '未命名层级' }}
                    </span>
                    <el-button type="danger" link v-if="idx < workspaceConfig.tiers.length - 1" @click="removeTier(idx)">删除此层级</el-button>
                    <el-tag v-else type="info" size="small">剩余学生自动进入这一层</el-tag>
                  </div>
                  
                  <el-row :gutter="15" style="margin-top: 15px;">
                    <el-col :span="8">
                      <el-form-item label="这一层叫什么">
                        <el-input v-model="tier.name" placeholder="例如：强基班" @change="saveWorkspaceConfig" />
                      </el-form-item>
                    </el-col>
                    <el-col :span="16">
                      <el-form-item label="这一层包含哪些班">
                        <el-select v-model="tier.class_names" multiple collapse-tags placeholder="选择班级" @change="handleTierClassNamesChange(idx)" style="width: 100%;">
                          <el-option v-for="cls in allClassNames" :key="cls" :label="cls" :value="cls" />
                        </el-select>
                      </el-form-item>
                    </el-col>
                  </el-row>

                  <!-- 每班目标人数设置 -->
                  <div v-if="tier.class_names && tier.class_names.length > 1" class="class-sizes-panel">
                    <div class="class-sizes-header">
                      <el-icon style="color: #e6a23c; margin-right: 5px;"><Setting /></el-icon>
                      <span>每班目标人数 <em style="color: #999; font-size: 12px; font-style: normal;">（不填就自动平均）</em></span>
                    </div>
                    <el-row :gutter="10" style="margin-top: 10px;">
                      <el-col v-for="cls in tier.class_names" :key="cls" :span="Math.max(4, Math.min(8, Math.floor(24 / tier.class_names.length)))" style="margin-bottom: 8px;">
                        <div class="class-size-item">
                          <div class="class-size-label">{{ cls }}</div>
                          <el-input-number
                            v-model="tier.class_sizes[cls]"
                            :min="0" :max="5000"
                            :placeholder="'自动'"
                            controls-position="right"
                            style="width: 100%;"
                            @change="saveWorkspaceConfig"
                          />
                        </div>
                      </el-col>
                    </el-row>
                  </div>
                  
                  <el-row :gutter="15" v-if="idx < workspaceConfig.tiers.length - 1">
                    <el-col :span="8">
                      <el-form-item label="怎么选出这一层学生">
                        <el-select v-model="tier.filter_type" @change="handleFilterTypeChange(idx)" style="width: 100%;">
                          <el-option label="按名次截断" value="rank" />
                          <el-option label="按分数条件" value="condition" />
                        </el-select>
                      </el-form-item>
                    </el-col>
                    
                    <el-col :span="16" v-if="tier.filter_type === 'rank'">
                      <el-row :gutter="10">
                        <el-col :span="12">
                          <el-form-item label="按哪个成绩排序">
                            <el-select v-model="tier.sort_key" @change="saveWorkspaceConfig" style="width: 100%;">
                              <el-option label="总分" value="总分" />
                              <el-option v-for="subj in availableSubjects" :key="subj" :label="subj" :value="subj" />
                            </el-select>
                          </el-form-item>
                        </el-col>
                        <el-col :span="12">
                          <el-form-item label="这一层招收人数">
                            <el-input-number v-model="tier.filter_rank_cutoff" :min="1" :max="5000" @change="saveWorkspaceConfig" style="width: 100%;" />
                            <div class="tip-text" style="line-height: 1.3; margin-top: 4.0px; color: #e6a23c; font-size: 11px;">
                              填这一层实际人数，不是累计名次。例如第二层再招 50 人，这里填 50。
                            </div>
                          </el-form-item>
                        </el-col>
                      </el-row>
                    </el-col>
                    
                    <el-col :span="16" v-if="tier.filter_type === 'condition'">
                      <el-form-item label="满足什么分数条件">
                        <div class="conditions-list">
                          <div v-for="(cond, cIdx) in tier.filter_conditions" :key="cIdx" class="condition-row" style="margin-bottom: 5px; display: flex; align-items: center;">
                            <el-select v-model="cond.field" placeholder="科目" style="width: 120px; margin-right: 5px;" @change="saveWorkspaceConfig">
                              <el-option label="总分" value="总分" />
                              <el-option v-for="subj in availableSubjects" :key="subj" :label="subj" :value="subj" />
                            </el-select>
                            <el-select v-model="cond.operator" placeholder="比较" style="width: 90px; margin-right: 5px;" @change="saveWorkspaceConfig">
                              <el-option label="≥" value=">=" />
                              <el-option label=">" value=">" />
                              <el-option label="≤" value="<=" />
                              <el-option label="<" value="<" />
                              <el-option label="=" value="==" />
                            </el-select>
                            <el-input v-model="cond.value" placeholder="分数值" style="width: 100px; margin-right: 5px;" @change="saveWorkspaceConfig" />
                            <el-button type="danger" link @click="removeCondition(idx, cIdx)">
                              <el-icon><Delete /></el-icon>
                            </el-button>
                          </div>
                          <el-button type="primary" link @click="addCondition(idx)" style="padding: 0;">
                            <el-icon><Plus /></el-icon> 添加条件
                          </el-button>
                        </div>
                      </el-form-item>
                    </el-col>
                  </el-row>
                </div>
                
                <el-button type="success" plain style="width: 100%; border-style: dashed; margin-top: 10px;" @click="addTier">
                  <el-icon style="margin-right: 5px;"><Plus /></el-icon> 新增一层班级
                </el-button>
              </div>

              <el-form-item>
                <el-button type="primary" size="large" :loading="workspace?.status === 'running'" class="run-algo-btn" @click="startAlgorithm">
                  <el-icon><VideoPlay /></el-icon> 确认无误，开始分班
                </el-button>
              </el-form-item>
            </el-form>
          </div>

          <!-- Console & Progress -->
          <div v-if="showProgressPanel" class="algo-console-card">
            <h3>分班进度</h3>
            <div class="progress-block" style="margin-top: 20px;">
              <div class="labels">
                <span>当前进度</span>
                <span>{{ workspace?.status === 'success' ? 100 : (workspace?.progress || 0) }}%</span>
              </div>
              <el-progress 
                :percentage="workspace?.status === 'success' ? 100 : (workspace?.progress || 0)" 
                :status="workspace?.status === 'failed' ? 'exception' : (workspace?.status === 'success' ? 'success' : '')"
                :stroke-width="12"
              />
              <div v-if="workspace?.status === 'running'" style="margin-top: 10px; display: flex; justify-content: flex-end;">
                <el-button type="danger" size="small" plain @click="handleResetStatus">
                  重置/强制中止状态
                </el-button>
              </div>
            </div>
            
            <div class="console-box-wrapper">
              <div class="console-header">
                <span class="red-dot"></span>
                <span class="yellow-dot"></span>
                <span class="green-dot"></span>
                <span class="title">处理记录</span>
              </div>
              <div class="console-content" ref="consoleBox">
                <div v-for="(log, index) in logs" :key="index" class="log-line">
                  <span class="log-time">[{{ log.time }}]</span>
                  <span class="log-msg" :class="log.type">{{ log.message }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="step-navigation-buttons">
          <el-button @click="activeTab = 'rules'">上一步：设置特殊要求</el-button>
          <el-button type="primary" :disabled="workspace?.status !== 'success'" @click="activeTab = 'result'">下一步：查看和导出</el-button>
        </div>
      </el-tab-pane>

      <!-- Tab 4: Finetuning and Charts -->
      <el-tab-pane name="result" label="4 查看和导出" lazy>
        <div class="result-dashboard">
          <div class="result-actions-row">
            <div class="left-btns">
              <el-button type="success" @click="exportExcel">
                <el-icon><Download /></el-icon> 导出分班结果Excel
              </el-button>
              <el-button type="primary" plain @click="openBackupDialog">
                <el-icon><Camera /></el-icon> 创建备份镜像
              </el-button>
              <el-button type="info" plain @click="backupListVisible = true">
                <el-icon><RefreshRight /></el-icon> 恢复历史备份 ({{ backups.length }})
              </el-button>
            </div>
            <div class="right-btns">
              <el-button type="warning" size="default" @click="toggleSunshineLive(true)">
                <el-icon><FullScreen /></el-icon> 开启阳光分班大屏
              </el-button>
            </div>
          </div>

          <!-- Stats Overview Grid -->
          <div class="stats-overview-grid">
            <div v-for="stat in classStats" :key="stat.class_name" class="stat-class-card">
              <h4>{{ stat.class_name }}</h4>
              <div class="class-summary">
                <div class="c-size">人数：<strong>{{ stat.size }}</strong></div>
                <div class="c-gender">男/女：{{ stat.males }} / {{ stat.females }} ({{ stat.male_ratio }}%)</div>
              </div>
              <div class="class-avg-list">
                <div v-for="(avg, subj) in stat.averages" :key="subj" class="c-avg-item">
                  <span class="sub-name">{{ subj }}均分</span>
                  <span class="sub-val">
                    {{ avg }}
                    <span style="font-size: 11px; color: #10b981; margin-left: 6px; font-weight: 600;" v-if="stat.top10ratios && stat.top10ratios[subj] !== undefined">
                      (Top 10%: {{ stat.top10ratios[subj] }}%)
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- ========== 分班花名册（按班展示完整学生名单）========== -->
          <div class="roster-section">
            <div class="roster-header">
              <h3>📋 分班花名册</h3>
              <div class="roster-controls">
                <div class="roster-filter-item">
                  <span class="control-label">查看班级:</span>
                  <el-select
                    v-model="rosterClassFilter"
                    placeholder="全部班级"
                    clearable
                    style="width: 140px;"
                  >
                    <el-option label="全部班级" value="" />
                    <el-option v-for="c in allClassNames" :key="c" :label="c" :value="c" />
                  </el-select>
                </div>
                <el-input
                  v-model="rosterSearch"
                  placeholder="搜索学生姓名..."
                  clearable
                  :prefix-icon="Search"
                  style="width: 220px"
                  @input="handleRosterSearch"
                />
                <el-button-group style="margin-left: 10px;">
                  <el-button :type="rosterViewMode === 'table' ? 'primary' : 'default'" @click="rosterViewMode = 'table'">
                    表格视图
                  </el-button>
                  <el-button :type="rosterViewMode === 'card' ? 'primary' : 'default'" @click="rosterViewMode = 'card'">
                    卡片视图
                  </el-button>
                </el-button-group>
              </div>
            </div>

            <!-- 卡片视图：横向排列每班 -->
            <div v-if="rosterViewMode === 'card'" class="roster-cards-wrapper">
              <div v-for="cls in filteredRosterClassNames" :key="cls" class="roster-class-panel">
                <div class="roster-class-header">
                  <span class="roster-cls-name">{{ cls }}</span>
                  <span class="roster-cls-count">{{ getRosterStudents(cls).length }} 人</span>
                </div>
                <div class="roster-student-list">
                  <div
                    v-for="s in getRosterStudents(cls)"
                    :key="s.id"
                    class="roster-stu-item"
                    :class="{ 'is-locked': s.is_locked, 'highlight-search': rosterSearch && s.name.includes(rosterSearch) }"
                  >
                    <span class="rst-name">{{ s.name }}</span>
                    <el-tag
                      :type="getGenderTagType(s.gender)"
                      size="small"
                      class="rst-gender"
                    >{{ s.gender }}</el-tag>
                    <el-icon v-if="s.is_locked" class="rst-lock" title="已锁定"><Lock /></el-icon>
                  </div>
                  <div v-if="getRosterStudents(cls).length === 0" class="roster-empty">暂无学生</div>
                </div>
              </div>
            </div>

            <!-- 表格视图：完整信息表格，按班分组 -->
            <div v-if="rosterViewMode === 'table'">
              <el-tabs type="border-card" class="roster-class-tabs">
                <!-- 全部学生 Tab 页 (仅在显示全部班级且未筛选单个班级时展示) -->
                <el-tab-pane
                  v-if="!rosterClassFilter"
                  label="全部学生"
                  lazy
                >
                  <el-table
                    :data="getRosterStudents('')"
                    border
                    stripe
                    size="small"
                    style="width: 100%"
                    :max-height="480"
                  >
                    <el-table-column type="index" label="序号" width="60" align="center" />
                    <el-table-column prop="student_id" label="学号" width="120" />
                    <el-table-column prop="name" label="姓名" width="110">
                      <template #default="{ row }">
                        <span :class="{ 'name-highlight': rosterSearch && row.name.includes(rosterSearch) }">{{ row.name }}</span>
                      </template>
                    </el-table-column>
                    <el-table-column prop="gender" label="性别" width="70" align="center">
                      <template #default="{ row }">
                        <el-tag :type="getGenderTagType(row.gender)" size="small">{{ row.gender }}</el-tag>
                      </template>
                    </el-table-column>
                    <!-- 动态展示各科分数，类似 Excel -->
                    <el-table-column
                      v-for="subj in availableSubjects"
                      :key="'roster_all_score_' + subj"
                      :label="subj"
                      width="90"
                      align="center"
                    >
                      <template #default="{ row }">
                        <span>{{ row.scores?.[subj] !== undefined ? row.scores[subj] : '-' }}</span>
                      </template>
                    </el-table-column>

                    <el-table-column label="特色标签" width="160">
                      <template #default="{ row }">
                        <el-tag
                          v-for="tag in row.custom_tags"
                          :key="tag"
                          size="small"
                          type="success"
                          effect="plain"
                          style="margin-right: 3px"
                        >{{ tag }}</el-tag>
                        <span v-if="!row.custom_tags?.length" class="muted-text">-</span>
                      </template>
                    </el-table-column>

                    <!-- 动态展示自定义扩展字段，类似 Excel -->
                    <el-table-column
                      v-for="key in allExtraKeys"
                      :key="'roster_all_extra_' + key"
                      :label="key"
                      min-width="110"
                      align="center"
                    >
                      <template #default="{ row }">
                        <span>{{ row.extra_fields?.[key] !== undefined ? row.extra_fields[key] : '-' }}</span>
                      </template>
                    </el-table-column>

                    <!-- 分配班级列 -->
                    <el-table-column prop="assigned_class" label="分配班级" width="110" align="center">
                      <template #default="{ row }">
                        <span :class="{ 'assigned-text': row.assigned_class, 'unassigned-text': !row.assigned_class }">
                          {{ row.assigned_class || '未分配' }}
                        </span>
                      </template>
                    </el-table-column>

                    <el-table-column label="状态" width="80" align="center">
                      <template #default="{ row }">
                        <el-tag v-if="row.is_locked" type="warning" size="small">已锁定</el-tag>
                        <span v-else class="muted-text">-</span>
                      </template>
                    </el-table-column>
                  </el-table>
                </el-tab-pane>

                <!-- 各班级 Tab 页 -->
                <el-tab-pane
                  v-for="cls in filteredRosterClassNames"
                  :key="cls"
                  :label="`${cls} (${getRosterStudents(cls).length}人)`"
                  lazy
                >
                  <el-table
                    :data="getRosterStudents(cls)"
                    border
                    stripe
                    size="small"
                    style="width: 100%"
                    :max-height="480"
                  >
                    <el-table-column type="index" label="序号" width="60" align="center" />
                    <el-table-column prop="student_id" label="学号" width="120" />
                    <el-table-column prop="name" label="姓名" width="110">
                      <template #default="{ row }">
                        <span :class="{ 'name-highlight': rosterSearch && row.name.includes(rosterSearch) }">{{ row.name }}</span>
                      </template>
                    </el-table-column>
                    <el-table-column prop="gender" label="性别" width="70" align="center">
                      <template #default="{ row }">
                        <el-tag :type="getGenderTagType(row.gender)" size="small">{{ row.gender }}</el-tag>
                      </template>
                    </el-table-column>
                    <!-- 动态展示各科分数，类似 Excel -->
                    <el-table-column
                      v-for="subj in availableSubjects"
                      :key="'roster_score_' + subj"
                      :label="subj"
                      width="90"
                      align="center"
                    >
                      <template #default="{ row }">
                        <span>{{ row.scores?.[subj] !== undefined ? row.scores[subj] : '-' }}</span>
                      </template>
                    </el-table-column>

                    <el-table-column label="特色标签" width="160">
                      <template #default="{ row }">
                        <el-tag
                          v-for="tag in row.custom_tags"
                          :key="tag"
                          size="small"
                          type="success"
                          effect="plain"
                          style="margin-right: 3px"
                        >{{ tag }}</el-tag>
                        <span v-if="!row.custom_tags?.length" class="muted-text">-</span>
                      </template>
                    </el-table-column>

                    <!-- 动态展示自定义扩展字段，类似 Excel -->
                    <el-table-column
                      v-for="key in allExtraKeys"
                      :key="'roster_extra_' + key"
                      :label="key"
                      min-width="110"
                      align="center"
                    >
                      <template #default="{ row }">
                        <span>{{ row.extra_fields?.[key] !== undefined ? row.extra_fields[key] : '-' }}</span>
                      </template>
                    </el-table-column>
                    <el-table-column label="状态" width="80" align="center">
                      <template #default="{ row }">
                        <el-tag v-if="row.is_locked" type="warning" size="small">已锁定</el-tag>
                        <span v-else class="muted-text">-</span>
                      </template>
                    </el-table-column>
                  </el-table>
                </el-tab-pane>
              </el-tabs>
            </div>
          </div>

          <!-- Finetuning Table and swap recommend helper -->
          <div class="finetuning-container">
            <div class="students-table-section">
              <h3>手动调班微调 (支持智能互换推荐)</h3>
              
              <div class="finetuning-filters">
                <el-select v-model="fineClassA" placeholder="选择班级 A" style="width: 140px" @change="refreshFineStudents">
                  <el-option v-for="c in allClassNames" :key="c" :label="c" :value="c" />
                </el-select>
                <span class="swap-icon">🔄</span>
                <el-select v-model="fineClassB" placeholder="选择班级 B" style="width: 140px" @change="refreshFineStudents">
                  <el-option v-for="c in allClassNames" :key="c" :label="c" :value="c" />
                </el-select>
              </div>

              <div class="dual-tables-row">
                <!-- Class A Table -->
                <div class="class-table-box">
                  <h4>{{ fineClassA || '班级 A' }} 名单 ({{ studentsA.length }}人)</h4>
                  <el-table :data="studentsA" border size="small" highlight-current-row @current-change="handleSelectStudentA" height="350px">
                    <el-table-column prop="name" label="姓名" width="100" />
                    <el-table-column prop="gender" label="性别" width="60" />
                    <el-table-column label="成绩">
                      <template #default="{ row }">
                        <span v-for="(val, key) in row.scores" :key="key" style="margin-right:8px; font-size:11px;">
                          {{ key }}:{{ val }}
                        </span>
                      </template>
                    </el-table-column>
                  </el-table>
                </div>

                <!-- Class B Table -->
                <div class="class-table-box">
                  <h4>{{ fineClassB || '班级 B' }} 名单 ({{ studentsB.length }}人)</h4>
                  <el-table :data="studentsB" border size="small" highlight-current-row @current-change="handleSelectStudentB" height="350px">
                    <el-table-column prop="name" label="姓名" width="100" />
                    <el-table-column prop="gender" label="性别" width="60" />
                    <el-table-column label="成绩">
                      <template #default="{ row }">
                        <span v-for="(val, key) in row.scores" :key="key" style="margin-right:8px; font-size:11px;">
                          {{ key }}:{{ val }}
                        </span>
                      </template>
                    </el-table-column>
                  </el-table>
                </div>
              </div>

              <!-- Manual Swap Trigger -->
              <div class="manual-swap-trigger-row" style="margin-top: 15px;">
                <el-button type="primary" :disabled="!selectedStudentA || !selectedStudentB" @click="executeManualSwap">
                  <el-icon><Refresh /></el-icon> 互换所选学生 ({{ selectedStudentA?.name }} 🔄 {{ selectedStudentB?.name }})
                </el-button>
              </div>
            </div>

            <!-- Smart Swap Suggestion Side Panel -->
            <div class="swap-suggestion-section">
              <h3>智能对换匹配推荐</h3>
              <p class="section-desc">当您需要强行将某一学生调入新班级时，选择该学生和目标班级，系统将快速计算并推荐 1-3 个最适合换回来的候选学生，以保证整体均分、性别、人数影响降到最低。</p>
              
              <el-form label-position="top" style="margin-top: 15px;">
                <el-form-item label="待调班学生">
                  <el-select v-model="recommendStudentId" placeholder="选择学生" filterable style="width: 100%" @change="loadSwapRecommendations">
                    <el-option v-for="s in fullStudents" :key="s.id" :label="`${s.name} (${s.assigned_class || '未分班'}, ${s.gender})`" :value="s.id" />
                  </el-select>
                </el-form-item>
                <el-form-item label="目标班级">
                  <el-select v-model="recommendTargetClass" placeholder="选择目标班级" style="width: 100%" @change="loadSwapRecommendations">
                    <el-option v-for="c in allClassNames" :key="c" :label="c" :value="c" />
                  </el-select>
                </el-form-item>
              </el-form>

              <div v-loading="recommendsLoading" class="recommend-results">
                <div v-if="recommendations.length === 0" class="empty-recommends">
                  暂无推荐，请先选择待调班学生与目标班级。
                </div>
                <div v-else class="recommend-list">
                  <div v-for="(rec, idx) in recommendations" :key="idx" class="recommend-card">
                    <div class="rec-title-row">
                      <h4>推荐对换人选：{{ rec.student.name }}</h4>
                      <el-tag size="small" type="success">代价增量: {{ rec.cost_impact.toFixed(1) }}</el-tag>
                    </div>
                    <div class="rec-details">
                      <p>性别：{{ rec.student.gender }} | 当前班级：{{ rec.student.assigned_class }}</p>
                      <p>对换对班级指标影响：</p>
                      <ul>
                        <li v-for="(imp, key) in rec.details" :key="key" style="font-size:11px; color:#64748b;">
                          {{ key }}: {{ imp }}
                        </li>
                      </ul>
                    </div>
                    <div class="rec-footer">
                      <el-button type="primary" size="small" @click="executeRecommendSwap(rec.student.id)">
                        执行此互换
                      </el-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="step-navigation-buttons">
          <el-button @click="activeTab = 'divide'">上一步：重新分班配置</el-button>
          <el-button type="success" @click="goBack">完成并返回列表</el-button>
        </div>
      </el-tab-pane>
    </el-tabs>

    <!-- Excel Import Dialog -->
    <el-dialog v-model="importDialogVisible" title="批量导入分班学生" width="480px" destroy-on-close>
      <el-form label-position="top">
        <el-form-item label="导入模式">
          <el-radio-group v-model="importMode">
            <el-radio label="overwrite">覆盖模式（清空原名单）</el-radio>
            <el-radio label="append">追加模式（增量导入）</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="去重规则（追加模式下生效）" v-if="importMode === 'append'">
          <el-radio-group v-model="importDedup">
            <el-radio label="update">重复时覆盖旧数据</el-radio>
            <el-radio label="ignore">重复时保留旧数据（忽略新记录）</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="上传 Excel 文件">
          <el-upload
            class="upload-demo"
            drag
            action=""
            :auto-upload="false"
            :limit="1"
            :on-change="handleFileChange"
          >
            <el-icon class="el-icon--upload"><upload-filled /></el-icon>
            <div class="el-upload__text">
              将文件拖到此处，或<em>点击上传</em>
            </div>
            <template #tip>
              <div class="el-upload__tip">
                仅支持 xlsx/xls 格式文件，首行必须为表头且包含姓名与性别列。
              </div>
            </template>
          </el-upload>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="importDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="importing" @click="submitImport">开始导入</el-button>
      </template>
    </el-dialog>

    <!-- Student Edit Dialog -->
    <el-dialog v-model="studentDialogVisible" :title="isStudentEdit ? '编辑学生信息' : '添加学生'" width="500px">
      <el-form :model="studentForm" :rules="studentRules" ref="studentFormRef" label-position="top">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="姓名" prop="name">
              <el-input v-model="studentForm.name" placeholder="请输入姓名" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="性别" prop="gender">
              <el-radio-group v-model="studentForm.gender">
                <el-radio label="男">男</el-radio>
                <el-radio label="女">女</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="学号/考号">
          <el-input v-model="studentForm.student_id" placeholder="请输入学号（可选）" />
        </el-form-item>

        <!-- Scores Dynamic list -->
        <div class="form-section-title">分数成绩 (可留空)</div>
        <el-row :gutter="10" v-for="subj in scoreInputSubjects" :key="subj" style="margin-bottom:10px;">
          <el-col :span="12" class="score-label-col">
            <span>{{ subj }}分数：</span>
          </el-col>
          <el-col :span="12">
            <el-input-number v-model="studentForm.scores[subj]" :min="0" :max="150" style="width:100%" />
          </el-col>
        </el-row>
        <div v-if="scoreInputSubjects.length === 0" class="muted-text" style="font-size:12px; margin-bottom:15px;">
          当前未设置分班科目，可回到分班列表页配置。
        </div>

        <el-form-item label="特色标签">
          <el-select
            v-model="studentForm.custom_tags"
            multiple
            filterable
            allow-create
            default-first-option
            placeholder="输入回车可动态创建新标签（如：特长、寄宿）"
            style="width: 100%"
          />
        </el-form-item>

        <div class="form-section-title">其他信息 (可留空)</div>
        <div v-for="(item, index) in extraFieldRows" :key="index" class="extra-field-row">
          <el-input v-model="item.key" placeholder="字段名，如联系电话" />
          <el-input v-model="item.value" placeholder="内容" />
          <el-button type="danger" link :icon="Delete" @click="removeExtraField(index)" />
        </div>
        <el-button type="primary" link class="add-extra-btn" @click="addExtraField">
          <el-icon><Plus /></el-icon> 添加其他信息
        </el-button>
      </el-form>
      <template #footer>
        <el-button @click="studentDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="studentSubmitting" @click="submitStudentForm">确定</el-button>
      </template>
    </el-dialog>

    <!-- Rule Dialog -->
    <el-dialog v-model="ruleDialogVisible" title="创建分班预设规则" width="500px">
      <el-form :model="ruleForm" :rules="ruleRules" ref="ruleFormRef" label-position="top">
        <el-form-item label="规则类型" prop="rule_type">
          <el-select v-model="ruleForm.rule_type" placeholder="请选择规则类型" style="width: 100%">
            <el-option label="预分班级 (强行分配入某班)" value="pre_assign" />
            <el-option label="排除班级 (不分入特定班级)" value="exclude" />
            <el-option label="组合绑定 (同班强行在一起)" value="group" />
            <el-option label="互斥打散 (强行分开到不同班)" value="mutex" />
          </el-select>
        </el-form-item>

        <el-form-item label="涉及学生姓名列表" prop="student_names">
          <el-select
            v-model="ruleForm.student_names"
            multiple
            filterable
            placeholder="输入姓名搜索并选中"
            style="width: 100%"
          >
            <el-option v-for="s in fullStudents" :key="s.id" :label="s.name" :value="s.name" />
          </el-select>
          <div class="tip-text">可多选。同班、互斥规则请务必选择 2 人及以上。</div>
        </el-form-item>

        <el-form-item label="目标班级" prop="target_class" v-if="ruleForm.rule_type === 'pre_assign' || ruleForm.rule_type === 'exclude'">
          <el-select v-model="ruleForm.target_class" placeholder="请选择对应班级" style="width: 100%">
            <el-option v-for="c in allClassNames" :key="c" :label="c" :value="c" />
          </el-select>
        </el-form-item>

        <el-form-item label="规则备注">
          <el-input v-model="ruleForm.description" placeholder="说明该规则的用途" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="ruleDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="ruleSubmitting" @click="submitRuleForm">添加</el-button>
      </template>
    </el-dialog>

    <!-- Create Backup snapshot Dialog -->
    <el-dialog v-model="backupDialogVisible" title="创建备份镜像快照" width="460px">
      <el-form label-position="top">
        <el-form-item label="备份版本名称">
          <el-input v-model="backupName" placeholder="例如：初始蛇形分配备份、调班微调第一版" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="backupDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="backupSubmitting" @click="submitBackup">保存备份</el-button>
      </template>
    </el-dialog>

    <!-- Backup List Restore Dialog -->
    <el-dialog v-model="backupListVisible" title="恢复历史快照镜像" width="550px">
      <p class="dialog-desc">恢复快照将覆盖当前的分班锁定状态、分配的班级名册及预设规则，请谨慎操作。</p>
      <el-table :data="backups" border style="width: 100%; margin-top: 15px;">
        <el-table-column prop="name" label="备份名称" />
        <el-table-column prop="created_at" label="备份时间" width="170">
          <template #default="{ row }">
            {{ formatDateTime(row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" align="center">
          <template #default="{ row }">
            <el-button type="warning" size="small" @click="restoreBackup(row.id)">
              还原
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <!-- Import Rules JSON Dialog -->
    <el-dialog v-model="batchPreAssignVisible" title="批量指定学生预分班" width="520px" destroy-on-close>
      <el-alert title="请先下载模板，按“一行一名学生”填写目标班级后上传。模板会根据当前学生数据决定是否包含学号列。" type="info" :closable="false" show-icon />
      <el-form label-position="top" style="margin-top: 18px;">
        <el-form-item label="上传批量设置 Excel 文件">
          <el-upload
            class="upload-demo"
            drag
            action=""
            accept=".xlsx,.xlsm"
            :auto-upload="false"
            :limit="1"
            :on-change="handleBatchPreAssignFileChange"
          >
            <el-icon class="el-icon--upload"><upload-filled /></el-icon>
            <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
            <template #tip>
              <div class="el-upload__tip">仅支持 .xlsx 文件；导入前会校验学生、班级和重复指定。</div>
            </template>
          </el-upload>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button link type="primary" @click="downloadBatchPreAssignTemplate">重新下载模板</el-button>
        <el-button @click="batchPreAssignVisible = false">取消</el-button>
        <el-button type="primary" :loading="batchPreAssigning" @click="submitBatchPreAssign">开始导入</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="importRulesVisible" title="导入分班规则 (JSON)" width="450px" destroy-on-close>
      <el-form label-position="top">
        <el-form-item label="请上传规则配置文件">
          <el-upload
            class="upload-demo"
            drag
            action=""
            :auto-upload="false"
            :limit="1"
            :on-change="handleRulesFileChange"
          >
            <el-icon class="el-icon--upload"><upload-filled /></el-icon>
            <div class="el-upload__text">
              将规则 JSON 文件拖到此处，或<em>点击上传</em>
            </div>
          </el-upload>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="importRulesVisible = false">取消</el-button>
        <el-button type="primary" :loading="importingRules" @click="submitImportRules">开始导入</el-button>
      </template>
    </el-dialog>

    <!-- Sunshine Live Screen Overlay -->
    <transition name="fade">
      <div v-if="sunshineVisible" class="sunshine-live-overlay">
        <!-- Close overlay button -->
        <el-button circle class="close-overlay-btn" :icon="Close" @click="toggleSunshineLive(false)"></el-button>

        <div class="sunshine-container animate__animated animate__fadeInUp">
          <header class="sunshine-header">
            <div class="header-logo">爱教务 🌟 阳光分班</div>
            <h2>{{ workspace?.name }} — 阳光分班实况投屏大屏</h2>
            <div class="control-panel">
              <span class="ctrl-label">滚动速度:</span>
              <el-slider v-model="scrollSpeed" :min="1" :max="10" class="speed-slider"></el-slider>
              <el-button type="primary" size="small" @click="toggleScrollState">
                {{ scrollingActive ? '暂停滚动' : '继续滚动' }}
              </el-button>
            </div>
          </header>

          <!-- Class Columns Scrolling Wrapper -->
          <div class="sunshine-columns-wrapper" ref="scrollerContainer">
            <div class="sunshine-class-column" v-for="stat in classStats" :key="stat.class_name">
              <div class="col-header">
                <h3>{{ stat.class_name }}</h3>
                <div class="col-badges">
                  <span class="badge size">人数: {{ stat.size }}</span>
                  <span class="badge gender">男/女: {{ stat.males }}/{{ stat.females }}</span>
                </div>
              </div>
              
              <!-- Students scrolling list -->
              <div class="col-student-list">
                <div 
                  v-for="s in getStudentsInClass(stat.class_name)" 
                  :key="s.id" 
                  class="student-item-card"
                >
                  <span class="stu-name">{{ s.name }}</span>
                  <span class="stu-gender" :class="s.gender">{{ s.gender }}</span>
                  <span v-if="s.student_id" class="stu-id">{{ s.student_id }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, nextTick, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { 
  ArrowLeft, Search, Plus, Edit, Delete, Warning, CircleCheck, 
  Download, Upload, Cpu, DataAnalysis, FullScreen, Close, 
  VideoPlay, RefreshRight, Camera, Refresh, Setting, Lock,
  Operation, QuestionFilled
} from '@element-plus/icons-vue'
import { UploadFilled } from '@element-plus/icons-vue'
import { divisionApi } from '@/api'
import { ElMessage, ElMessageBox } from 'element-plus'

const route = useRoute()
const router = useRouter()
const workspaceId = Number(route.params.workspaceId)

// Loadings
const pageLoading = ref(false)
const submitsLoading = ref(false)
const recommendsLoading = ref(false)

// Data state
const workspace = ref(null)
const students = ref([])
const rules = ref([])
const backups = ref([])
const divisionSubjects = ref([])
const activeTab = ref('students')

// Filter/Search
const searchQuery = ref('')
const filterClass = ref('')
const filteredStudents = ref([])
const currentPage = ref(1)
const pageSize = ref(20)
const totalStudents = ref(0)
const fullStudents = ref([])

// Config & Setup
const availableSubjects = ref([])
const selectedSubjects = ref([])
const scoreInputSubjects = computed(() => {
  const merged = new Set([...(selectedSubjects.value || []), ...(availableSubjects.value || [])])
  return Array.from(merged)
})
const pageSizesOption = computed(() => {
  const base = [10, 20, 50, 100, 200, 500, 1000]
  if (totalStudents.value > 0 && !base.includes(totalStudents.value)) {
    base.push(totalStudents.value)
  }
  return Array.from(new Set(base)).sort((a, b) => a - b)
})
const workspaceConfig = ref({
  algorithm: 'anneal',
  iterations: 20000,
  divisionMode: 'parallel',
  tiers: [],
  weights: {
    score: 10,
    gender: 5,
    tags: 2,
    surname: 5,
    top_students: 10
  }
})

const balanceScoreEnabled = computed({
  get: () => (workspaceConfig.value.weights?.score ?? 10) > 0,
  set: (val) => {
    if (!workspaceConfig.value.weights) {
      workspaceConfig.value.weights = { score: 10, gender: 5, tags: 2 }
    }
    workspaceConfig.value.weights.score = val ? 10 : 0
    saveWorkspaceConfig()
  }
})

const balanceGenderEnabled = computed({
  get: () => (workspaceConfig.value.weights?.gender ?? 5) > 0,
  set: (val) => {
    if (!workspaceConfig.value.weights) {
      workspaceConfig.value.weights = { score: 10, gender: 5, tags: 2 }
    }
    workspaceConfig.value.weights.gender = val ? 5 : 0
    saveWorkspaceConfig()
  }
})

const balanceTagsEnabled = computed({
  get: () => (workspaceConfig.value.weights?.tags ?? 2) > 0,
  set: (val) => {
    if (!workspaceConfig.value.weights) {
      workspaceConfig.value.weights = { score: 10, gender: 5, tags: 2, surname: 5 }
    }
    workspaceConfig.value.weights.tags = val ? 2 : 0
    saveWorkspaceConfig()
  }
})

const balanceSurnameEnabled = computed({
  get: () => (workspaceConfig.value.weights?.surname ?? 5) > 0,
  set: (val) => {
    if (!workspaceConfig.value.weights) {
      workspaceConfig.value.weights = { score: 10, gender: 5, tags: 2, surname: 5, top_students: 10 }
    }
    workspaceConfig.value.weights.surname = val ? 5 : 0
    saveWorkspaceConfig()
  }
})

const balanceTopStudentsEnabled = computed({
  get: () => (workspaceConfig.value.weights?.top_students ?? 10) > 0,
  set: (val) => {
    if (!workspaceConfig.value.weights) {
      workspaceConfig.value.weights = { score: 10, gender: 5, tags: 2, surname: 5, top_students: 10 }
    }
    workspaceConfig.value.weights.top_students = val ? 10 : 0
    saveWorkspaceConfig()
  }
})

// Progress log polling
const logs = ref([])
const timer = ref(null)
const consoleBox = ref(null)

// Smart Swap Finetuning state
const fineClassA = ref('')
const fineClassB = ref('')
const studentsA = ref([])
const studentsB = ref([])
const selectedStudentA = ref(null)
const selectedStudentB = ref(null)

const recommendStudentId = ref(null)
const recommendTargetClass = ref('')
const recommendations = ref([])

// Roster Search and View Mode
const rosterSearch = ref('')
const rosterClassFilter = ref('')
const rosterViewMode = ref('table')


// Excel Dialogs
const importDialogVisible = ref(false)
const importMode = ref('overwrite')
const importDedup = ref('update')
const importFile = ref(null)
const importing = ref(false)

// Student CRUD Dialog
const studentDialogVisible = ref(false)
const isStudentEdit = ref(false)
const editStudentId = ref(null)
const studentFormRef = ref(null)
const studentSubmitting = ref(false)
const studentForm = ref({
  name: '',
  gender: '男',
  student_id: '',
  scores: {},
  custom_tags: [],
  extra_fields: {},
  assigned_class: null,
  is_locked: false
})
const extraFieldRows = ref([])

const studentRules = {
  name: [{ required: true, message: '请输入学生姓名', trigger: 'blur' }],
  gender: [{ required: true, message: '请选择性别', trigger: 'change' }]
}

// Rules CRUD dialog
const ruleDialogVisible = ref(false)
const ruleFormRef = ref(null)
const ruleSubmitting = ref(false)
const ruleForm = ref({
  rule_type: 'pre_assign',
  student_names: [],
  target_class: '',
  description: ''
})

const ruleRules = {
  rule_type: [{ required: true, message: '请选择规则类型', trigger: 'change' }],
  student_names: [{ required: true, message: '请输入至少一个学生', trigger: 'change' }]
}

// Backup dialog
const backupDialogVisible = ref(false)
const backupName = ref('')
const backupSubmitting = ref(false)
const backupListVisible = ref(false)

// Rules JSON file upload
const importRulesVisible = ref(false)
const rulesFile = ref(null)
const importingRules = ref(false)

// Batch pre-assign Excel upload
const batchPreAssignVisible = ref(false)
const batchPreAssignFile = ref(null)
const batchPreAssigning = ref(false)

// Conflict
const conflictError = ref('')

// Sunshine screen scroll
const sunshineVisible = ref(false)
const scrollSpeed = ref(3)
const scrollingActive = ref(true)
const scrollerContainer = ref(null)
let scrollTimer = null

onMounted(async () => {
  await fetchAllData()
  // Automatically select first two classes for finetuning if available
  if (allClassNames.value?.length >= 2) {
    fineClassA.value = allClassNames.value[0]
    fineClassB.value = allClassNames.value[1]
    refreshFineStudents()
  }
})

onBeforeUnmount(() => {
  stopProgressPolling()
  stopSunshineScroll()
})

const goBack = () => {
  router.push('/division')
}

// Global data loading
const fetchAllData = async () => {
  pageLoading.value = true
  try {
    const [wsData, rData, bData, subjectData] = await Promise.all([
      divisionApi.getWorkspace(workspaceId),
      divisionApi.getRules(workspaceId),
      divisionApi.getBackups(workspaceId),
      divisionApi.getSubjects()
    ])
    workspace.value = wsData
    divisionSubjects.value = subjectData
    
    // Config values
    workspaceConfig.value = {
      algorithm: wsData.config?.algorithm || 'anneal',
      iterations: wsData.config?.iterations || 20000,
      divisionMode: wsData.config?.divisionMode || 'parallel',
      tiers: (wsData.config?.tiers || []).map(t => ({
        ...t,
        class_sizes: t.class_sizes || {}
      })),
      weights: {
        score: 10,
        gender: 5,
        tags: 2,
        surname: 5,
        top_students: 10,
        ...(wsData.config?.weights || {})
      }
    }
    const enabledSubjectNames = (subjectData || []).filter(s => s.is_enabled).map(s => s.name)
    selectedSubjects.value = wsData.config?.balance_targets?.length ? wsData.config.balance_targets : enabledSubjectNames
    rules.value = rData
    backups.value = bData
    
    // Fetch paginated students and full students
    const promises = [fetchPaginatedStudents(), fetchFullStudents()]
    await Promise.all(promises)
    
    // Load division subject settings, then merge scores found in existing students for compatibility.
    const sourceStudents = fullStudents.value.length > 0 ? fullStudents.value : students.value
    const subjects = new Set((subjectData || []).filter(s => s.is_enabled).map(s => s.name))
    sourceStudents.forEach(s => {
      if (s.scores) {
        Object.keys(s.scores).forEach(k => subjects.add(k))
      }
    })
    availableSubjects.value = Array.from(subjects)
    
    // Re-check conflicts
    checkConflictsOffline()
    
    // If status is running, keep polling
    if (wsData.status === 'running') {
      startProgressPolling()
    } else {
      // Add a default log
      logs.value = [
        { time: new Date().toLocaleTimeString(), message: `工作区加载成功。状态：${getStatusText(wsData.status)}`, type: 'info' }
      ]
      if (wsData.error_message) {
        logs.value.push({ time: new Date().toLocaleTimeString(), message: wsData.error_message, type: 'error' })
      }
    }
  } catch (error) {
    console.error(error)
  } finally {
    pageLoading.value = false
  }
}

const handleTabChange = (tabName) => {
  if (tabName === 'result') {
    refreshFineStudents()
  }
}

// 本地冲突检测（仅拦截后端仲裁无法处理的硬冲突：同一学生被预分到多个不同班级）
const checkConflictsOffline = () => {
  conflictError.value = ''
  const studentNames = new Set(students.value.map(s => s.name))
  const preAssigns = {}

  rules.value.forEach(r => {
    const names = r.student_names.filter(n => studentNames.has(n))
    if (names.length === 0) return
    
    // 唯一不可仲裁的硬冲突：同一学生被预分到多个不同班级
    if (r.rule_type === 'pre_assign') {
      names.forEach(name => {
        if (preAssigns[name] && preAssigns[name] !== r.target_class) {
          conflictError.value = `学生【${name}】同时被预分到不同的班级（${preAssigns[name]} 和 ${r.target_class}），此冲突无法自动仲裁，请检查规则。`
        }
        preAssigns[name] = r.target_class
      })
    }
  })
  // 注：互斥+同班、预分班+排除等冲突，后端会自动按优先级仲裁并输出警告日志，无需前端阻断分班。
}

const fetchPaginatedStudents = async () => {
  try {
    const params = {
      page: currentPage.value,
      limit: pageSize.value
    }
    if (searchQuery.value) {
      params.name = searchQuery.value.trim()
    }
    if (filterClass.value) {
      params.assigned_class = filterClass.value
    }
    const res = await divisionApi.getStudents(workspaceId, params)
    students.value = res.items || []
    totalStudents.value = res.total || 0
    filteredStudents.value = students.value
  } catch (error) {
    console.error('Error fetching paginated students:', error)
  }
}

const fetchFullStudents = async () => {
  try {
    const res = await divisionApi.getStudents(workspaceId)
    fullStudents.value = res || []
  } catch (error) {
    console.error('Error fetching full students:', error)
  }
}

// Student Filters & pagination
const filterStudents = async () => {
  currentPage.value = 1
  await fetchPaginatedStudents()
}

const paginatedStudents = computed(() => {
  return students.value
})

const handleSizeChange = async (val) => {
  pageSize.value = val
  currentPage.value = 1
  await fetchPaginatedStudents()
}

const handleCurrentChange = async (val) => {
  currentPage.value = val
  await fetchPaginatedStudents()
}

const toggleStudentLock = async (row) => {
  try {
    await divisionApi.updateStudent(workspaceId, row.id, { is_locked: row.is_locked })
    ElMessage.success(`学生锁定状态已更新`)
  } catch (error) {
    row.is_locked = !row.is_locked // Revert
  }
}

// Download Import Excel template
const downloadTemplate = async () => {
  try {
    const blob = await divisionApi.downloadTemplate()
    const url = window.URL.createObjectURL(new Blob([blob]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'student_division_template.xlsx')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error) {
    console.error('Download template failed:', error)
  }
}

const downloadBatchPreAssignTemplate = async () => {
  try {
    const blob = await divisionApi.downloadBatchPreAssignTemplate(workspaceId)
    const url = window.URL.createObjectURL(new Blob([blob]))
    const link = document.createElement('a')
    link.href = url
    link.download = `batch_pre_assign_template_${workspaceId}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error(error)
    const detail = error?.response?.data?.detail
      || (typeof error?.response?.data === 'string' ? error.response.data : '')
      || error?.message
    ElMessage.error(detail ? `批量设置模板下载失败：${detail}` : '批量设置模板下载失败，请重试')
  }
}

// File importing Excel
const handleFileChange = (uploadFile) => {
  importFile.value = uploadFile.raw
}

const submitImport = async () => {
  if (!importFile.value) {
    ElMessage.warning('请先选择要导入的 Excel 文件')
    return
  }
  importing.value = true
  try {
    const res = await divisionApi.importStudentsExcel(workspaceId, importFile.value, importMode.value, importDedup.value)
    ElMessage.success(res.message || '导入成功')
    importDialogVisible.value = false
    importFile.value = null
    await fetchAllData()
  } catch (error) {
    console.error(error)
  } finally {
    importing.value = false
  }
}

// Student dialog operations
const openStudentDialog = (row = null) => {
  if (row) {
    isStudentEdit.value = true
    editStudentId.value = row.id
    studentForm.value = {
      name: row.name,
      gender: row.gender,
      student_id: row.student_id || '',
      scores: { ...row.scores },
      custom_tags: [ ...row.custom_tags ],
      extra_fields: { ...(row.extra_fields || {}) },
      assigned_class: row.assigned_class,
      is_locked: row.is_locked
    }
    scoreInputSubjects.value.forEach(s => {
      if (studentForm.value.scores[s] === undefined) studentForm.value.scores[s] = null
    })
    extraFieldRows.value = Object.entries(row.extra_fields || {}).map(([key, value]) => ({ key, value }))
  } else {
    isStudentEdit.value = false
    editStudentId.value = null
    studentForm.value = {
      name: '',
      gender: '男',
      student_id: '',
      scores: {},
      custom_tags: [],
      extra_fields: {},
      assigned_class: null,
      is_locked: false
    }
    extraFieldRows.value = []
    // Initialize scores mapping
    scoreInputSubjects.value.forEach(s => {
      studentForm.value.scores[s] = 80
    })
  }
  studentDialogVisible.value = true
}

const syncExtraFieldRows = () => {
  const fields = {}
  extraFieldRows.value.forEach(item => {
    const key = String(item.key || '').trim()
    const value = item.value
    if (key && value !== undefined && value !== null && String(value).trim() !== '') {
      fields[key] = String(value).trim()
    }
  })
  studentForm.value.extra_fields = fields
}

const addExtraField = () => {
  extraFieldRows.value.push({ key: '', value: '' })
}

const removeExtraField = (index) => {
  extraFieldRows.value.splice(index, 1)
  syncExtraFieldRows()
}

const submitStudentForm = async () => {
  if (!studentFormRef.value) return
  await studentFormRef.value.validate(async (valid) => {
    if (!valid) return
    syncExtraFieldRows()
    studentSubmitting.value = true
    try {
      if (isStudentEdit.value) {
        await divisionApi.updateStudent(workspaceId, editStudentId.value, studentForm.value)
        ElMessage.success('学生信息已更新')
      } else {
        await divisionApi.createStudent(workspaceId, studentForm.value)
        ElMessage.success('已添加学生')
      }
      studentDialogVisible.value = false
      await fetchAllData()
    } catch (error) {
      console.error(error)
    } finally {
      studentSubmitting.value = false
    }
  })
}

const deleteStudent = (studentId) => {
  ElMessageBox.confirm('确定要删除该学生吗？', '提示', { type: 'warning' }).then(async () => {
    try {
      await divisionApi.deleteStudent(workspaceId, studentId)
      ElMessage.success('删除成功')
      await fetchAllData()
    } catch (error) {
      console.error(error)
    }
  })
}

const clearAllStudents = () => {
  ElMessageBox.confirm('这将会清空全部录入的学生信息！确定继续吗？', '极度警告', {
    confirmButtonText: '确定清空',
    cancelButtonText: '取消',
    type: 'danger'
  }).then(async () => {
    try {
      await divisionApi.clearAllStudents(workspaceId)
      ElMessage.success('名册已清空')
      await fetchAllData()
    } catch (error) {
      console.error(error)
    }
  })
}

// Rules setup operations
const getRuleTagType = (type) => {
  switch (type) {
    case 'pre_assign': return 'success'
    case 'exclude': return 'danger'
    case 'group': return 'primary'
    case 'mutex': return 'warning'
    default: return 'info'
  }
}

const getRuleTypeText = (type) => {
  switch (type) {
    case 'pre_assign': return '指定预分班'
    case 'exclude': return '不分入某班'
    case 'group': return '组合绑定(同班)'
    case 'mutex': return '互斥打散(分流)'
    default: return type
  }
}

const openRuleDialog = () => {
  ruleForm.value = {
    rule_type: 'pre_assign',
    student_names: [],
    target_class: '',
    description: ''
  }
  ruleDialogVisible.value = true
}

const submitRuleForm = async () => {
  if (!ruleFormRef.value) return
  await ruleFormRef.value.validate(async (valid) => {
    if (!valid) return
    ruleSubmitting.value = true
    try {
      await divisionApi.createRule(workspaceId, ruleForm.value)
      ElMessage.success('规则已添加')
      ruleDialogVisible.value = false
      await fetchAllData()
    } catch (error) {
      console.error(error)
    } finally {
      ruleSubmitting.value = false
    }
  })
}

const deleteRule = (ruleId) => {
  ElMessageBox.confirm('确定要删除该分班规则吗？', '提示', { type: 'warning' }).then(async () => {
    try {
      await divisionApi.deleteRule(workspaceId, ruleId)
      ElMessage.success('规则已删除')
      await fetchAllData()
    } catch (error) {
      console.error(error)
    }
  })
}

const handleRulesFileChange = (uploadFile) => {
  rulesFile.value = uploadFile.raw
}

const submitImportRules = async () => {
  if (!rulesFile.value) {
    ElMessage.warning('请先选择要导入的规则 JSON 配置文件')
    return
  }
  importingRules.value = true
  try {
    const res = await divisionApi.importRulesJson(workspaceId, rulesFile.value)
    ElMessage.success(res.message || '导入成功')
    importRulesVisible.value = false
    rulesFile.value = null
    await fetchAllData()
  } catch (error) {
    console.error(error)
  } finally {
    importingRules.value = false
  }
}

const exportRules = async () => {
  try {
    const response = await divisionApi.exportRulesJson(workspaceId)
    const blob = new Blob([response.data], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `rules_workspace_${workspaceId}.json`
    document.body.appendChild(a)
    a.click()
    URL.revokeObjectURL(url)
    document.body.removeChild(a)
  } catch (e) {
    ElMessage.error('规则导出失败，请重试')
  }
}

const handleBatchPreAssignFileChange = (uploadFile) => {
  batchPreAssignFile.value = uploadFile.raw
}

const submitBatchPreAssign = async () => {
  if (!batchPreAssignFile.value) {
    ElMessage.warning('请先选择要导入的 Excel 文件')
    return
  }
  batchPreAssigning.value = true
  try {
    const res = await divisionApi.importBatchPreAssignExcel(workspaceId, batchPreAssignFile.value)
    ElMessage.success(res.message || '批量指定成功')
    batchPreAssignVisible.value = false
    batchPreAssignFile.value = null
    await fetchAllData()
  } catch (error) {
    console.error(error)
  } finally {
    batchPreAssigning.value = false
  }
}

// Computed helper for classes
const allClassNames = computed(() => {
  const wsClasses = workspace.value?.config?.class_names || []
  const sourceStudents = fullStudents.value.length > 0 ? fullStudents.value : students.value
  const studentClasses = Array.from(new Set(
    sourceStudents.map(s => s.assigned_class).filter(Boolean)
  ))
  const combined = [...wsClasses]
  studentClasses.forEach(cls => {
    if (!combined.includes(cls)) {
      combined.push(cls)
    }
  })
  
  // 如果存在未分配班级的学生，追加“未分配”虚拟班级
  const hasUnassigned = sourceStudents.some(s => !s.assigned_class)
  if (hasUnassigned) {
    combined.push('未分配')
  }
  
  return combined
})

const filteredRosterClassNames = computed(() => {
  if (rosterClassFilter.value) {
    return [rosterClassFilter.value]
  }
  return allClassNames.value
})

const allExtraKeys = computed(() => {
  const keys = new Set()
  const sourceStudents = fullStudents.value.length > 0 ? fullStudents.value : students.value
  sourceStudents.forEach(s => {
    if (s.extra_fields) {
      Object.keys(s.extra_fields).forEach(k => keys.add(k))
    }
  })
  return Array.from(keys)
})

const guideSteps = computed(() => {
  const hasStudents = totalStudents.value > 0
  const hasResult = workspace.value?.status === 'success'
  return [
    {
      key: 'students',
      tab: 'students',
      index: 1,
      title: '学生名单',
      hint: hasStudents ? `已录入 ${students.value.length} 人` : '先导入 Excel 或手动添加',
      active: activeTab.value === 'students',
      done: hasStudents
    },
    {
      key: 'rules',
      tab: 'rules',
      index: 2,
      title: '特殊要求',
      hint: rules.value.length > 0 ? `已设置 ${rules.value.length} 条` : '可跳过',
      active: activeTab.value === 'rules',
      done: !conflictError.value
    },
    {
      key: 'divide',
      tab: 'divide',
      index: 3,
      title: '开始分班',
      hint: workspace.value?.status === 'running' ? '正在分班' : (hasResult ? '已完成' : '确认后点击开始'),
      active: activeTab.value === 'divide',
      done: hasResult
    },
    {
      key: 'result',
      tab: 'result',
      index: 4,
      title: '查看和导出',
      hint: hasResult ? '可导出 Excel' : '完成后查看',
      active: activeTab.value === 'result',
      done: hasResult
    }
  ]
})

const currentGuide = computed(() => {
  if (workspace.value?.status === 'running') {
    return { title: '正在分班，请稍等', desc: '系统会持续更新进度。完成后会自动进入结果页面。' }
  }
  if (workspace.value?.status === 'success') {
    return { title: '分班已完成', desc: '可以查看每个班的名单、做少量手动调整，也可以直接导出 Excel。' }
  }
  if (conflictError.value) {
    return { title: '请先处理特殊要求冲突', desc: '有学生被设置了互相矛盾的要求，处理后再开始分班。' }
  }
  if (students.value.length === 0) {
    return { title: '第一步：导入学生名单', desc: '建议下载模板后用 Excel 批量导入；名单导入后再设置特殊要求和开始分班。' }
  }
  return { title: '名单已准备好', desc: '如有指定同班、分开、固定班级等要求，请先设置；没有特殊要求可以直接开始分班。' }
})

const showProgressPanel = computed(() => {
  return workspace.value?.status === 'running' || workspace.value?.status === 'failed'
})

// Config targets save
const saveWorkspaceConfig = async () => {
  try {
    const payload = {
      name: workspace.value.name,
      config: {
        ...workspace.value.config,
        algorithm: workspaceConfig.value.algorithm,
        iterations: workspaceConfig.value.iterations,
        balance_targets: selectedSubjects.value,
        divisionMode: workspaceConfig.value.divisionMode,
        tiers: workspaceConfig.value.divisionMode === 'tiered' ? workspaceConfig.value.tiers : [],
        weights: workspaceConfig.value.weights
      }
    }
    const updated = await divisionApi.updateWorkspace(workspaceId, payload)
    workspace.value = updated
  } catch (error) {
    console.error(error)
  }
}

const handleModeChange = () => {
  if (workspaceConfig.value.divisionMode === 'tiered' && (!workspaceConfig.value.tiers || workspaceConfig.value.tiers.length === 0)) {
    const allCls = allClassNames.value
    workspaceConfig.value.tiers = [
      {
        name: '强基班',
        class_names: allCls.length > 0 ? [allCls[0]] : [],
        class_sizes: {},
        filter_type: 'rank',
        sort_key: '总分',
        filter_rank_cutoff: 50,
        filter_conditions: []
      },
      {
        name: '实验班',
        class_names: allCls.length > 1 ? [allCls[1]] : [],
        class_sizes: {},
        filter_type: 'rank',
        sort_key: '总分',
        filter_rank_cutoff: 50,
        filter_conditions: []
      },
      {
        name: '平行班',
        class_names: allCls.slice(2),
        class_sizes: {},
        filter_type: 'all',
        sort_key: '总分',
        filter_conditions: []
      }
    ]
  }
  saveWorkspaceConfig()
}

const addTier = () => {
  const newTier = {
    name: '新分层班',
    class_names: [],
    class_sizes: {},
    filter_type: 'rank',
    sort_key: '总分',
    filter_rank_cutoff: 100,
    filter_conditions: []
  }
  if (!workspaceConfig.value.tiers) {
    workspaceConfig.value.tiers = []
  }
  if (workspaceConfig.value.tiers.length > 0) {
    workspaceConfig.value.tiers.splice(workspaceConfig.value.tiers.length - 1, 0, newTier)
  } else {
    workspaceConfig.value.tiers.push(newTier)
  }
  saveWorkspaceConfig()
}

const handleTierClassNamesChange = (idx) => {
  const tier = workspaceConfig.value.tiers[idx]
  if (!tier.class_sizes) tier.class_sizes = {}
  // 删除不再属于该层级的班级的尺寸配置
  const validClasses = new Set(tier.class_names)
  for (const cls of Object.keys(tier.class_sizes)) {
    if (!validClasses.has(cls)) {
      delete tier.class_sizes[cls]
    }
  }
  saveWorkspaceConfig()
}

const removeTier = (idx) => {
  workspaceConfig.value.tiers.splice(idx, 1)
  saveWorkspaceConfig()
}

const handleFilterTypeChange = (idx) => {
  const tier = workspaceConfig.value.tiers[idx]
  if (tier.filter_type === 'condition' && (!tier.filter_conditions || tier.filter_conditions.length === 0)) {
    tier.filter_conditions = [{ field: availableSubjects.value[0] || '总分', operator: '>=', value: '100' }]
  }
  saveWorkspaceConfig()
}

const addCondition = (tierIdx) => {
  const tier = workspaceConfig.value.tiers[tierIdx]
  if (!tier.filter_conditions) {
    tier.filter_conditions = []
  }
  tier.filter_conditions.push({ field: availableSubjects.value[0] || '总分', operator: '>=', value: '100' })
  saveWorkspaceConfig()
}

const removeCondition = (tierIdx, condIdx) => {
  const tier = workspaceConfig.value.tiers[tierIdx]
  tier.filter_conditions.splice(condIdx, 1)
  saveWorkspaceConfig()
}

// Algorithm background execution & Polling progress
const startAlgorithm = async () => {
  if (students.value.length === 0) {
    ElMessage.warning('学生名册为空，无法分班')
    return
  }
  if (conflictError.value) {
    ElMessage.error(`存在规则冲突！请在“预设规则”页解决冲突后再进行分班。`)
    return
  }
  
  try {
    await divisionApi.startDivision(workspaceId)
    ElMessage.success('已向后台发送请求，分班引擎启动成功！')
    
    // Update local model status
    if (workspace.value) {
      workspace.value.status = 'running'
      workspace.value.progress = 5
      workspace.value.log_message = '启动成功，初始化算例模型...'
    }
    
    logs.value = [
      { time: new Date().toLocaleTimeString(), message: '⚡ 智能分班后台计算进程启动...', type: 'info' }
    ]
    
    startProgressPolling()
  } catch (error) {
    console.error(error)
  }
}

const handleResetStatus = async () => {
  try {
    await ElMessageBox.confirm('确定要强制重置分班生成状态吗？这将强行中断或终止当前卡在“正在分班”的任务。', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    await divisionApi.resetWorkspaceStatus(workspaceId)
    ElMessage.success('分班状态已成功强制重置')
    // Refresh workspace details
    const wsData = await divisionApi.getWorkspace(workspaceId)
    workspace.value = wsData
  } catch (error) {
    if (error !== 'cancel') {
      console.error(error)
    }
  }
}

const startProgressPolling = () => {
  if (timer.value) return
  
  timer.value = setInterval(async () => {
    try {
      const wsData = await divisionApi.getWorkspace(workspaceId)
      workspace.value = wsData
      
      // Push new log preview
      const previewMsg = wsData.log_message || ''
      if (previewMsg) {
        const lastLog = logs.value[logs.value.length - 1]
        if (!lastLog || lastLog.message !== previewMsg) {
          logs.value.push({
            time: new Date().toLocaleTimeString(),
            message: previewMsg,
            type: wsData.status === 'failed' ? 'error' : 'info'
          })
          // Auto scroll console
          nextTick(() => {
            if (consoleBox.value) {
              consoleBox.value.scrollTop = consoleBox.value.scrollHeight
            }
          })
        }
      }
      
      if (wsData.status !== 'running') {
        stopProgressPolling()
        if (wsData.status === 'success') {
          ElMessage.success('🎉 智能分班计算大功告成！已成功完成分配与优化。')
          await fetchAllData() // Reload everything
          activeTab.value = 'result' // Switch to dashboard
        } else if (wsData.status === 'failed') {
          ElMessage.error(`分班运算遭遇错误：${wsData.error_message}`)
          logs.value.push({
            time: new Date().toLocaleTimeString(),
            message: `[ERROR] ${wsData.error_message}`,
            type: 'error'
          })
        }
      }
    } catch (e) {
      console.error('Error polling progress:', e)
    }
  }, 1000)
}

const stopProgressPolling = () => {
  if (timer.value) {
    clearInterval(timer.value)
    timer.value = null
  }
}

// Finetuning Class data
const refreshFineStudents = () => {
  const sourceStudents = fullStudents.value.length > 0 ? fullStudents.value : students.value
  
  if (fineClassA.value) {
    studentsA.value = sourceStudents.filter(s => s.assigned_class === fineClassA.value)
  } else {
    studentsA.value = []
  }
  
  if (fineClassB.value) {
    studentsB.value = sourceStudents.filter(s => s.assigned_class === fineClassB.value)
  } else {
    studentsB.value = []
  }
  
  selectedStudentA.value = null
  selectedStudentB.value = null
}

const handleSelectStudentA = (row) => {
  selectedStudentA.value = row
}

const handleSelectStudentB = (row) => {
  selectedStudentB.value = row
}

const executeManualSwap = async () => {
  if (!selectedStudentA.value || !selectedStudentB.value) return
  
  submitsLoading.value = true
  try {
    await divisionApi.executeSwap(workspaceId, selectedStudentA.value.id, selectedStudentB.value.id)
    ElMessage.success('手动互换调整成功')
    
    // Reload local student and stats
    await fetchAllData()
    refreshFineStudents()
    
    // Clear selections
    selectedStudentA.value = null
    selectedStudentB.value = null
  } catch (error) {
    console.error(error)
  } finally {
    submitsLoading.value = false
  }
}

// Smart recommendations
const loadSwapRecommendations = async () => {
  if (!recommendStudentId.value || !recommendTargetClass.value) {
    recommendations.value = []
    return
  }
  
  recommendsLoading.value = true
  try {
    const res = await divisionApi.getSwapRecommend(workspaceId, recommendStudentId.value, recommendTargetClass.value)
    recommendations.value = res
  } catch (error) {
    console.error(error)
  } finally {
    recommendsLoading.value = false
  }
}

const executeRecommendSwap = async (candidateId) => {
  if (!recommendStudentId.value) return
  
  try {
    await divisionApi.executeSwap(workspaceId, recommendStudentId.value, candidateId)
    ElMessage.success('智能对换推荐执行成功！')
    
    // Clear recommends parameters
    recommendStudentId.value = null
    recommendTargetClass.value = ''
    recommendations.value = []
    
    await fetchAllData()
    refreshFineStudents()
  } catch (error) {
    console.error(error)
  }
}

// Backups snapshots
const openBackupDialog = () => {
  backupName.value = ''
  backupDialogVisible.value = true
}

const submitBackup = async () => {
  if (!backupName.value) {
    ElMessage.warning('请输入备份名称')
    return
  }
  backupSubmitting.value = true
  try {
    await divisionApi.createBackup(workspaceId, { name: backupName.value })
    ElMessage.success('备份镜像快照已创建')
    backupDialogVisible.value = false
    await fetchAllData()
  } catch (error) {
    console.error(error)
  } finally {
    backupSubmitting.value = false
  }
}

const restoreBackup = async (backupId) => {
  ElMessageBox.confirm(
    '确认要还原此快照镜像吗？这将会清空当前的所有分配和规则，并替换为镜像状态。',
    '提示',
    { type: 'warning' }
  ).then(async () => {
    pageLoading.value = true
    try {
      await divisionApi.restoreBackup(workspaceId, backupId)
      ElMessage.success('镜像快照还原成功')
      backupListVisible.value = false
      await fetchAllData()
      refreshFineStudents()
    } catch (error) {
      console.error(error)
    } finally {
      pageLoading.value = false
    }
  })
}

// Export results
const exportExcel = async () => {
  try {
    const blob = await divisionApi.exportResult(workspaceId)
    const url = window.URL.createObjectURL(new Blob([blob]))
    const link = document.createElement('a')
    link.href = url
    
    const wsName = workspace.value?.name || `工作区_${workspaceId}`
    link.setAttribute('download', `分班结果_${wsName}.xlsx`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error) {
    console.error(error)
  }
}

// Class Stats computed helper
const classStats = computed(() => {
  const classNames = (allClassNames.value || []).filter(c => c !== '未分配')
  if (classNames.length === 0 || students.value.length === 0) return []
  
  const subjects = selectedSubjects.value.length > 0 ? selectedSubjects.value : availableSubjects.value
  const sourceStudents = fullStudents.value.length > 0 ? fullStudents.value : students.value
  
  // Calculate global top 10% student IDs for each subject
  const top10StudentIdsBySubject = {}
  subjects.forEach(subj => {
    const studentsWithScore = sourceStudents
      .filter(s => s.scores && s.scores[subj] !== undefined && s.scores[subj] !== null)
      .map(s => ({ id: s.id, score: parseFloat(s.scores[subj]) }))
    
    // Sort descending
    studentsWithScore.sort((a, b) => b.score - a.score)
    const cutoff = Math.max(1, Math.floor(studentsWithScore.length * 0.10))
    const topIds = new Set(studentsWithScore.slice(0, cutoff).map(item => item.id))
    top10StudentIdsBySubject[subj] = topIds
  })
  
  return classNames.map(c_name => {
    const c_students = studentsByClassMap.value[c_name] || []
    const size = c_students.length
    const males = c_students.filter(s => s.gender === '男').length
    const females = size - males
    const male_ratio = size > 0 ? Math.round(males / size * 100) : 0
    
    const averages = {}
    const top10ratios = {}
    
    subjects.forEach(subj => {
      const studentScores = c_students.filter(s => s.scores && s.scores[subj] !== undefined && s.scores[subj] !== null)
      if (studentScores.length > 0) {
        const sum = studentScores.reduce((acc, s) => acc + s.scores[subj], 0)
        averages[subj] = (sum / studentScores.length).toFixed(2)
      } else {
        averages[subj] = '0.00'
      }
      
      // Calculate top 10% ratio for this class for this subject
      const topIds = top10StudentIdsBySubject[subj]
      if (topIds && topIds.size > 0 && studentScores.length > 0) {
        const topCount = studentScores.filter(s => topIds.has(s.id)).length
        top10ratios[subj] = Math.round((topCount / studentScores.length) * 100)
      } else {
        top10ratios[subj] = 0
      }
    })
    
    return {
      class_name: c_name,
      size,
      males,
      females,
      male_ratio,
      averages,
      top10ratios
    }
  })
})

// Optimized computed properties to cache student query and grouping maps, preventing UI lags
const studentsByClassMap = computed(() => {
  const map = {}
  const sourceStudents = fullStudents.value.length > 0 ? fullStudents.value : students.value
  sourceStudents.forEach(s => {
    const cls = s.assigned_class || '未分配'
    if (!map[cls]) {
      map[cls] = []
    }
    map[cls].push(s)
  })
  return map
})

const allRosterStudents = computed(() => {
  const search = rosterSearch.value.trim().toLowerCase()
  const sourceStudents = fullStudents.value.length > 0 ? fullStudents.value : students.value
  if (!search) return sourceStudents
  return sourceStudents.filter(s => 
    s.name.toLowerCase().includes(search) || 
    (s.student_id && s.student_id.toLowerCase().includes(search))
  )
})

const rosterStudentsMap = computed(() => {
  const map = {}
  const search = rosterSearch.value.trim().toLowerCase()
  const sourceStudents = fullStudents.value.length > 0 ? fullStudents.value : students.value
  sourceStudents.forEach(s => {
    if (search) {
      const matchName = s.name.toLowerCase().includes(search)
      const matchId = s.student_id && s.student_id.toLowerCase().includes(search)
      if (!matchName && !matchId) return
    }
    const cls = s.assigned_class || '未分配'
    if (!map[cls]) {
      map[cls] = []
    }
    map[cls].push(s)
  })
  return map
})

const getStudentsInClass = (className) => {
  return studentsByClassMap.value[className] || []
}

const getRosterStudents = (className) => {
  if (!className || className === 'all' || className === '全部学生') {
    return allRosterStudents.value
  }
  return rosterStudentsMap.value[className] || []
}

const handleRosterSearch = () => {
  // Reactive recomputation is triggered when rosterSearch value changes
}

// Sunshine Live Screen Layout scroll speed & scrolling logic
const toggleSunshineLive = (visible) => {
  sunshineVisible.value = visible
  if (visible) {
    nextTick(() => {
      startSunshineScroll()
    })
  } else {
    stopSunshineScroll()
  }
}

const toggleScrollState = () => {
  scrollingActive.value = !scrollingActive.value
  if (scrollingActive.value) {
    startSunshineScroll()
  } else {
    stopSunshineScroll()
  }
}

const startSunshineScroll = () => {
  stopSunshineScroll()
  if (!scrollingActive.value) return
  
  let offset = 0
  const speedScale = 0.5 // Scale down speed for smoother scrolling
  
  scrollTimer = setInterval(() => {
    const el = scrollerContainer.value
    if (!el) return
    
    // Scroll height of element vs visible client height
    const scrollMax = el.scrollHeight - el.clientHeight
    if (scrollMax <= 0) return
    
    offset += scrollSpeed.value * speedScale
    if (offset >= scrollMax) {
      // Smooth reset back to top with pause
      offset = 0
    }
    el.scrollTop = offset
  }, 30)
}

const stopSunshineScroll = () => {
  if (scrollTimer) {
    clearInterval(scrollTimer)
    scrollTimer = null
  }
}

const getStatusType = (status) => {
  switch (status) {
    case 'idle': return 'info'
    case 'running': return 'primary'
    case 'success': return 'success'
    case 'failed': return 'danger'
    default: return 'info'
  }
}

const getStatusText = (status) => {
  switch (status) {
    case 'idle': return '准备就绪'
    case 'running': return '正在分班'
    case 'success': return '已完成'
    case 'failed': return '运算失败'
    default: return status
  }
}

const getGenderTagType = (gender) => {
  if (gender === '男') return 'primary'
  if (gender === '女') return 'danger'
  return 'info'
}

const formatDateTime = (val) => {
  if (!val) return ''
  const d = new Date(val)
  return d.toLocaleString()
}
</script>

<style scoped>
.workspace-detail-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 10px 0 40px 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.step-navigation-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
}

.workspace-summary-card {
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #dbe4ef;
  padding: 22px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  box-shadow: 0 6px 20px rgba(20, 35, 60, 0.05);
}

.summary-left {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.back-link {
  font-weight: 500;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 0;
  margin-bottom: 4px;
}

.ws-info h2 {
  font-size: 22px;
  color: #1e293b;
  margin: 0 0 6px 0;
  font-weight: 750;
  letter-spacing: 0;
}

.ws-meta {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.meta-item {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

.summary-right {
  display: flex;
  align-items: center;
}

.guide-panel {
  background: #f8fbff;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  padding: 18px;
  margin-bottom: 18px;
}

.guide-title {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 18px;
  margin-bottom: 14px;
}

.guide-title h3 {
  margin: 0;
  color: #172033;
  font-size: 17px;
  font-weight: 750;
}

.guide-title p {
  margin: 4px 0 0 0;
  color: #536172;
  line-height: 1.5;
  font-size: 13px;
}

.guide-steps {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.guide-step {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 64px;
  border: 1px solid #dbe4ef;
  background: #ffffff;
  border-radius: 8px;
  padding: 10px 12px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
}

.guide-step:hover {
  border-color: #93c5fd;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.08);
}

.guide-step.active {
  border-color: #2563eb;
  background: #eff6ff;
}

.guide-step.done .step-index {
  background: #0f766e;
}

.step-index {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #64748b;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex: 0 0 auto;
}

.step-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.step-copy strong {
  color: #172033;
  font-size: 14px;
  font-weight: 750;
}

.step-copy small {
  color: #64748b;
  font-size: 12px;
  margin-top: 3px;
  line-height: 1.3;
}

.custom-tabs :deep(.el-tabs__item) {
  font-size: 14px;
  font-weight: 600;
  padding: 0 22px;
}

.custom-tabs :deep(.el-tabs__header) {
  display: none !important;
}

.custom-tabs :deep(.el-tabs__item.is-active) {
  color: #2563eb;
}

.tab-action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px;
  margin-top: 0;
  margin-bottom: 14px;
  flex-wrap: wrap;
  gap: 15px;
}

.bar-left {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.bar-right {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.scores-preview, .tags-preview, .extra-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.score-tag {
  border-radius: 4px;
}

.custom-tag {
  border-radius: 6px;
  font-weight: 500;
}

.extra-tag {
  border-radius: 6px;
  max-width: 170px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.assigned-text {
  color: #059669;
  font-weight: 700;
}

.unassigned-text {
  color: #94a3b8;
  font-style: italic;
}

.pagination-row {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.rules-container {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 18px;
  margin-top: 0;
}

.rules-left, .rules-right {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 20px;
}

.card-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.rules-actions {
  display: flex;
  gap: 8px;
}

.conflict-check-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  margin-top: 15px;
  border: 1px solid;
}

.conflict-check-card.no-conflict {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #065f46;
}

.conflict-check-card.no-conflict .status-icon {
  font-size: 24px;
  color: #10b981;
}

.conflict-check-card.has-conflict {
  background: #fef2f2;
  border-color: #fca5a5;
  color: #991b1b;
}

.conflict-check-card.has-conflict .status-icon {
  font-size: 24px;
  color: #ef4444;
}

.status-text h4 {
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 4px 0;
}

.status-text p {
  font-size: 12px;
  line-height: 1.5;
  margin: 0;
}

.rule-hint-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-top: 20px;
}

.rule-hint-box h4 {
  font-size: 13px;
  color: #334155;
  font-weight: 700;
  margin: 0 0 10px 0;
}

.rule-hint-box ul {
  padding-left: 20px;
  margin: 0;
}

.rule-hint-box li {
  font-size: 12px;
  line-height: 1.6;
  color: #64748b;
  margin-bottom: 8px;
}

.tip-text {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 4px;
}

.algorithm-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-top: 0;
}

.algorithm-container.single-column {
  grid-template-columns: minmax(0, 860px);
}

.algo-setup-card, .algo-console-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 20px;
}

.run-algo-btn {
  background: #2563eb !important;
  border: none !important;
  font-weight: 700;
  width: 100%;
  margin-top: 10px;
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.22);
}

.run-algo-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.28);
}

.algo-console-card .labels {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
}

/* Animated Dark Shell Output Console box styling */
.console-box-wrapper {
  margin-top: 24px;
  border-radius: 8px;
  background: #172033;
  border: 1px solid #334155;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.24);
  overflow: hidden;
}

.console-header {
  background: #1e293b;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  border-bottom: 1px solid #334155;
}

.console-header .red-dot { width: 10px; height: 10px; border-radius: 50%; background: #ef4444; }
.console-header .yellow-dot { width: 10px; height: 10px; border-radius: 50%; background: #f59e0b; }
.console-header .green-dot { width: 10px; height: 10px; border-radius: 50%; background: #10b981; }

.console-header .title {
  color: #cbd5e1;
  font-size: 12px;
  margin-left: 10px;
}

.console-content {
  height: 250px;
  padding: 16px;
  overflow-y: auto;
  font-family: Consolas, Monaco, "Courier New", monospace;
  font-size: 12px;
  line-height: 1.6;
}

.log-line {
  margin-bottom: 6px;
  display: flex;
  gap: 8px;
}

.log-time {
  color: #64748b;
  user-select: none;
}

.log-msg {
  color: #38bdf8;
  white-space: pre-wrap;
}

.log-msg.error {
  color: #f87171;
}

/* Result finetuning view styling */
.result-dashboard {
  margin-top: 0;
}

.result-actions-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  background: #ffffff;
  padding: 16px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  gap: 12px;
  flex-wrap: wrap;
}

.left-btns {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

/* Stats class card list */
.stats-overview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-class-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 18px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.01);
}

.stat-class-card h4 {
  font-size: 16px;
  font-weight: 750;
  color: #1e293b;
  margin: 0 0 10px 0;
  border-bottom: 2px solid #f1f5f9;
  padding-bottom: 6px;
}

.class-summary {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 12px;
  line-height: 1.5;
}

.class-summary strong {
  font-size: 16px;
  color: #1e293b;
}

.class-avg-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.c-avg-item {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
}

.c-avg-item .sub-name {
  color: #64748b;
}

.c-avg-item .sub-val {
  color: #1e293b;
  font-weight: 700;
}

/* Roster styles */
.roster-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 24px;
}

.roster-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.roster-header h3 {
  font-size: 18px;
  font-weight: 750;
  color: #1e293b;
  margin: 0;
}

.roster-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.roster-filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f1f5f9;
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  margin-right: 10px;
}

.control-label {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
}

.roster-cards-wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.roster-class-panel {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  max-height: 400px;
  display: flex;
  flex-direction: column;
}

.roster-class-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #e2e8f0;
  padding-bottom: 8px;
  margin-bottom: 12px;
}

.roster-cls-name {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.roster-cls-count {
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
}

.roster-student-list {
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 4px;
}

.roster-stu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  transition: all 0.2s ease;
}

.roster-stu-item.is-locked {
  border-left: 3px solid #eab308;
}

.roster-stu-item.highlight-search {
  background: #fef08a;
  border-color: #facc15;
}

.rst-name {
  font-weight: 600;
  color: #334155;
}

.rst-gender {
  margin-left: 8px;
}

.rst-lock {
  color: #eab308;
  font-size: 14px;
  margin-left: auto;
}

.roster-empty {
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  padding: 20px 0;
}

.roster-class-tabs {
  border-radius: 8px;
  overflow: hidden;
}

.score-chip {
  display: inline-block;
  background: #f1f5f9;
  color: #475569;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  margin-right: 6px;
  font-weight: 500;
}

.extra-chip {
  display: inline-block;
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  margin-right: 6px;
  margin-bottom: 4px;
}

.extra-field-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.6fr) 32px;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}

.add-extra-btn {
  padding-left: 0;
}

.name-highlight {
  background-color: #fef08a;
  padding: 2px 4px;
  border-radius: 2px;
  font-weight: bold;
}

/* Dual tables for adjust swaps */
.finetuning-container {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 18px;
}

.students-table-section, .swap-suggestion-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 20px;
}

.finetuning-filters {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 15px 0;
}

.swap-icon {
  font-size: 18px;
}

.dual-tables-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.class-table-box h4 {
  font-size: 13px;
  color: #475569;
  font-weight: 700;
  margin-bottom: 10px;
}

.section-desc {
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
  margin: 0;
}

.recommend-results {
  margin-top: 15px;
  border-top: 1px solid #f1f5f9;
  padding-top: 15px;
  min-height: 200px;
}

.empty-recommends {
  text-align: center;
  padding: 40px;
  color: #94a3b8;
  font-size: 12px;
}

.recommend-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  transition: all 0.2s ease;
}

.recommend-card:hover {
  border-color: #cbd5e1;
  background: #f1f5f9;
}

.rec-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.rec-title-row h4 {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.rec-details p {
  font-size: 12px;
  color: #475569;
  margin: 0 0 6px 0;
}

.rec-details ul {
  padding-left: 20px;
  margin: 0 0 10px 0;
}

.rec-footer {
  display: flex;
  justify-content: flex-end;
}

/* Dialog additions */
.dialog-desc {
  font-size: 12px;
  color: #ef4444;
  background: #fef2f2;
  border: 1px solid #fca5a5;
  padding: 12px;
  border-radius: 8px;
  line-height: 1.5;
  margin: 0;
}

.form-section-title {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  border-bottom: 1px dashed #e2e8f0;
  padding-bottom: 6px;
  margin: 15px 0 10px 0;
}

.score-label-col {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #334155;
}

@media (max-width: 1100px) {
  .workspace-summary-card {
    align-items: flex-start;
    flex-direction: column;
  }

  .summary-right {
    width: 100%;
  }

  .summary-right :deep(.el-button-group) {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .summary-right :deep(.el-button) {
    margin-left: 0 !important;
    border-radius: 7px !important;
  }

  .guide-steps,
  .algorithm-container,
  .rules-container,
  .finetuning-container,
  .dual-tables-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .guide-title {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }

  .tab-action-bar,
  .result-actions-row,
  .roster-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .bar-left,
  .bar-right,
  .left-btns,
  .right-btns {
    width: 100%;
  }

  .bar-left :deep(.el-input),
  .bar-left :deep(.el-select),
  .bar-right :deep(.el-button),
  .left-btns :deep(.el-button),
  .right-btns :deep(.el-button),
  .roster-controls :deep(.el-input) {
    width: 100% !important;
  }
}

/* Sunshine Live Overlay Fullscreen styling */
.sunshine-live-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: radial-gradient(circle at center, #0b0f19 0%, #030508 100%);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  padding: 40px;
  overflow: hidden;
}

.close-overlay-btn {
  position: absolute;
  top: 30px;
  right: 30px;
  z-index: 2002;
  font-size: 24px;
  background: rgba(255, 255, 255, 0.1) !important;
  color: #ffffff !important;
  border: none !important;
  transition: all 0.3s ease;
}

.close-overlay-btn:hover {
  background: rgba(239, 64, 64, 0.6) !important;
  transform: rotate(90deg);
}

.sunshine-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  max-width: 1600px;
}

.sunshine-header {
  border-bottom: 2px solid rgba(255, 255, 255, 0.08);
  padding-bottom: 24px;
  margin-bottom: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-logo {
  font-weight: 900;
  font-size: 24px;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #fbbf24 0%, #ec4899 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.sunshine-header h2 {
  font-size: 24px;
  font-weight: 800;
  margin: 0;
  text-align: center;
  color: #e2e8f0;
}

.control-panel {
  display: flex;
  align-items: center;
  gap: 16px;
  background: rgba(255, 255, 255, 0.05);
  padding: 8px 16px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.ctrl-label {
  font-size: 13px;
  color: #94a3b8;
}

.speed-slider {
  width: 120px;
}

/* Sunshine columns layout using premium glassmorphism styling */
.sunshine-columns-wrapper {
  display: flex;
  gap: 24px;
  flex: 1;
  overflow-y: auto;
  padding-bottom: 40px;
  align-items: flex-start;
  scroll-behavior: smooth;
}

.sunshine-class-column {
  flex: 1;
  min-width: 260px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

.col-header {
  background: rgba(255, 255, 255, 0.05);
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.col-header h3 {
  font-size: 18px;
  font-weight: 800;
  margin: 0 0 8px 0;
  color: #f1f5f9;
}

.col-badges {
  display: flex;
  gap: 8px;
}

.col-badges .badge {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
}

.col-badges .badge.size {
  background: rgba(56, 189, 248, 0.15);
  color: #38bdf8;
}

.col-badges .badge.gender {
  background: rgba(236, 72, 153, 0.15);
  color: #f472b6;
}

.col-student-list {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.student-item-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;
}

.student-item-card:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.1);
  transform: scale(1.02);
}

.stu-name {
  font-size: 14px;
  font-weight: 600;
  color: #f8fafc;
}

.stu-gender {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
}

.stu-gender.男 {
  background: rgba(56, 189, 248, 0.15);
  color: #38bdf8;
}

.stu-gender.女 {
  background: rgba(244, 63, 94, 0.15);
  color: #fb7185;
}

.stu-id {
  font-size: 11px;
  color: #64748b;
  font-family: monospace;
}

/* Tiered configuration styles */
.tiered-config-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 15px;
}

.tier-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.3s ease;
}

.tier-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

.tier-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 10px;
  margin-bottom: 10px;
}

.tier-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  display: inline-flex;
  align-items: center;
}

.condition-row {
  margin-bottom: 8px;
}

.class-sizes-panel {
  background: linear-gradient(135deg, #fffbeb 0%, #fef9ee 100%);
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 12px 16px;
  margin: 10px 0;
}

.class-sizes-header {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 4px;
}

.class-size-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.class-size-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  text-align: center;
  background: #fef3c7;
  border-radius: 4px;
  padding: 2px 6px;
}
</style>
